import { cn } from '@/lib/utils';
import { TrendingDown, TrendingUp } from 'lucide-react';

interface WoWBadgeProps {
  delta: number | null;
  /** Whether positive delta is good (green) vs bad (red). Default: true */
  positiveIsGood?: boolean;
  /** Units shown after the number, default "pp" (percentage points). Use "" for counts. */
  unit?: string;
  /** Decimal places for display. Default 1 (for pp). Use 0 for counts. */
  decimals?: number;
  className?: string;
}

/**
 * Week-over-week delta badge. Shows ±N pp with directional arrow and color.
 * - positiveIsGood=true (default): up=green, down=red (e.g. resolution rate)
 * - positiveIsGood=false: up=red, down=green (e.g. failure count)
 */
export function WoWBadge({
  delta,
  positiveIsGood = true,
  unit = 'pp',
  decimals = 1,
  className,
}: WoWBadgeProps) {
  if (delta === null || delta === undefined) return null;

  const isPositive = delta > 0;
  const isNeutral = decimals === 0 ? delta === 0 : Math.abs(delta) < 0.5;

  const isGood = isNeutral ? null : positiveIsGood ? isPositive : !isPositive;

  const colorClass = isNeutral
    ? 'text-muted-foreground'
    : isGood
      ? 'text-green-600 dark:text-green-400'
      : 'text-red-600 dark:text-red-400';

  const Icon = isPositive ? TrendingUp : TrendingDown;
  const sign = isPositive ? '+' : '';

  const formatted = decimals === 0 ? Math.round(delta).toString() : delta.toFixed(decimals);

  return (
    <span
      className={cn('inline-flex items-center gap-0.5 text-xs font-medium', colorClass, className)}
    >
      {!isNeutral && <Icon className="h-3 w-3" />}
      {sign}
      {formatted}
      {unit}
    </span>
  );
}
